import { StandardKeyboardEvent } from "../../../base/browser/keyboardEvent.js";
import { Emitter } from "../../../base/common/events.js";
import { Disposable, DisposableStore, toDisposable } from "../../../base/common/lifecycle.js";
import { ILogService } from "../../log/common/log.js";
import { IWindowsMainService } from "../../windows/common/windows.js";
import { IKeyboardService, isModifierKey, KeyCode, ModifierKey } from "../common/keyboardService.js";



export class BrowserKeyboardService extends Disposable implements IKeyboardService {

    isKeyPressed(key: KeyCode | ModifierKey): boolean {
        if (isModifierKey(key)) {
            switch (key) {
                case "Shift":
                    return this.keysPressed.has("ShiftLeft") || this.keysPressed.has("ShiftRight");
                case "Control":
                    return this.keysPressed.has("ControlLeft") || this.keysPressed.has("ControlRight");
                case "Alt":
                    return this.keysPressed.has("AltLeft") || this.keysPressed.has("AltRight");
                case "Meta":
                    return this.keysPressed.has("MetaLeft") || this.keysPressed.has("MetaRight");
            }
        }
        return this.keysPressed.has(key);
    }

    private keysPressed: Set<string> = new Set();

    private readonly _onKeyDown = new Emitter<StandardKeyboardEvent>();
    public readonly onKeyDown = this._onKeyDown.event;
    private readonly _onKeyUp = new Emitter<StandardKeyboardEvent>();
    public readonly onKeyUp = this._onKeyUp.event;
    private readonly _onKeyPress = new Emitter<StandardKeyboardEvent>();
    public readonly onKeyPress = this._onKeyPress.event;

    constructor(
        @ILogService private readonly logger: ILogService,
        @IWindowsMainService windowsMainService: IWindowsMainService
    ) {
        super();

        const originalWindow = globalThis.window;

        let lastWindow: Window | null = null;
        this._register(windowsMainService.onDidOpenWindow((evt) => {
            ;
            const window = evt.getWindow();
            lastWindow = window;
            logger.trace(`KeyboardService: Attaching to window events for window`);
            this.subscribeToWindowEvents(window);
        }));
        this._register(windowsMainService.onDidDestroyWindow((evt) => {
            const window = evt.getWindow();
            if (lastWindow === window) {
                lastWindow = null;
                logger.trace(`KeyboardService: Re-attaching to original window events`);
                this.subscribeToWindowEvents(originalWindow);
            }
        }));

        this.subscribeToWindowEvents(globalThis.window);
    }

    private _windowEvents: DisposableStore | null = null;
    
    private subscribeToWindowEvents(window: Window): void {

        // @TODO: we probably want to subscribe to all window events and then clients need to check if input events are in the expected window (e.g. main window for fly controls)

        this._windowEvents?.dispose();
        this._windowEvents = new DisposableStore();
        this._register(this._windowEvents);

        window.addEventListener('keydown', this.__onKeyDown, { capture: true });
        this._windowEvents.add(toDisposable(() => {
            window.removeEventListener('keydown', this.__onKeyDown, { capture: true });
        }));

        window.addEventListener('keyup', this.__onKeyUp, { capture: true });
        this._windowEvents.add(toDisposable(() => {
            window.removeEventListener('keyup', this.__onKeyUp, { capture: true });
        }));

        window.addEventListener('keypress', this.__onKeyPress, { capture: true });
        this._windowEvents.add(toDisposable(() => {
            window.removeEventListener('keypress', this.__onKeyPress, { capture: true });
        }));
    }

    private __onKeyDown = (event: KeyboardEvent) => {
        const evt = new StandardKeyboardEvent(event);
        this._onKeyDown.fire(evt);
        this.keysPressed.add(event.code);
    }
    private __onKeyUp = (event: KeyboardEvent) => {
        const evt = new StandardKeyboardEvent(event);
        this._onKeyUp.fire(evt);
        this.keysPressed.delete(event.code);
    }
    private __onKeyPress = (event: KeyboardEvent) => {
        const evt = new StandardKeyboardEvent(event);
        this._onKeyPress.fire(evt);
    }


}